/* eslint-disable no-restricted-syntax */

/**
 * Primitive color definitions
 * @see [Figma Colors](https://www.figma.com/design/56sl8nzEsswtU7qNn6drvG/Kos-v1?node-id=2248-906)
 */
export interface ColorScale {
  50?: string;
  100?: string;
  200?: string;
  300?: string;
  400?: string;
  500?: string;
  600?: string;
  700?: string;
  800?: string;
  900?: string;
  950?: string;
}

export interface PrimitiveColors {
  black: string;
  white: string;
  transparent: string;
  brandNeonGreen: string;
  brandBlue: string;

  blackAlpha: ColorScale;
  whiteAlpha: ColorScale;
  red: ColorScale;
  green: ColorScale;
  teal: ColorScale;
  blue: ColorScale;
  cyan: ColorScale;
  pink: ColorScale;
  gray: ColorScale;
  goldenGlow: ColorScale;
  titanWhite: ColorScale;
}

/**
 * Primitive color palette
 * @see [Figma Colors](https://www.figma.com/design/56sl8nzEsswtU7qNn6drvG/Kos-v1?node-id=2248-906)
 */
const colors: PrimitiveColors = {
  transparent: '#FFFFFF00',

  black: '#000000',

  white: '#FFFFFF',

  brandNeonGreen: '#0AF09D',

  brandBlue: '#4495FA',

  blackAlpha: {
    50: '#0000000D',
    100: '#0000001A',
    200: '#00000033',
    300: '#0000004D',
    400: '#00000066',
    500: '#00000080',
    600: '#00000099',
    700: '#000000B3',
    800: '#000000CC',
    900: '#000000E6',
    950: '#000000F2',
  },

  whiteAlpha: {
    50: '#FFFFFF0D',
    100: '#FFFFFF1A',
    200: '#FFFFFF33',
    300: '#FFFFFF4D',
    400: '#FFFFFF66',
    500: '#FFFFFF80',
    600: '#FFFFFF99',
    700: '#FFFFFFB3',
    800: '#FFFFFFCC',
    900: '#FFFFFFE6',
    950: '#FFFFFFF2',
  },

  gray: {
    50: '#fafafa',
    100: '#f4f4f5',
    200: '#e4e4e7',
    300: '#d4d4d8',
    400: '#a1a1aa',
    500: '#71717a',
    600: '#52525b',
    700: '#3f3f46',
    800: '#27272a',
    900: '#18181b',
    950: '#111111',
  },

  red: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#991919',
    800: '#511111',
    900: '#300c0c',
    950: '#1f0808',
  },

  green: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#116932',
    800: '#124a28',
    900: '#042713',
    950: '#03190c',
  },

  teal: {
    50: '#f0fdfa',
    100: '#ccfbf1',
    200: '#99f6e4',
    300: '#5eead4',
    400: '#2dd4bf',
    500: '#14b8a6',
    600: '#0d9488',
    700: '#0c5d56',
    800: '#114240',
    900: '#032726',
    950: '#021716',
  },

  blue: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#a3cfff',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#173da6',
    800: '#1a3478',
    900: '#14204a',
    950: '#0c142e',
  },

  cyan: {
    50: '#ecfeff',
    100: '#cffafe',
    200: '#a5f3fc',
    300: '#67e8f9',
    400: '#22d3ee',
    500: '#06b6d4',
    600: '#0891b2',
    700: '#0c5c72',
    800: '#134152',
    900: '#072a38',
    950: '#051b24',
  },

  pink: {
    50: '#fdf2f8',
    100: '#fce7f3',
    200: '#fbcfe8',
    300: '#f9a8d4',
    400: '#f472b6',
    500: '#ec4899',
    600: '#db2777',
    700: '#a41752',
    800: '#6d0e34',
    900: '#45061f',
    950: '#2c0514',
  },

  goldenGlow: {
    50: '#FFF9EB',
    100: '#FFF0C6',
    200: '#FFE08F',
    300: '#FFC74A',
    400: '#FFB020',
    500: '#F98C07',
    600: '#DD6602',
    700: '#B74506',
    800: '#94350C',
    900: '#7A2C0D',
    950: '#4A1A07',
  },

  titanWhite: {
    50: '#EEF0FF',
    100: '#E1E4FF',
    200: '#C7CBFE',
    300: '#A5A9FC',
    400: '#7060F2',
    500: '#6346E5',
    600: '#5C41D5',
    700: '#5538CA',
    800: '#4530A3',
    900: '#3B2E81',
    950: '#291F5D',
  },
};

export { colors };
